import { MihatoMark } from "@/common/components/MihatoMark";
import { useAuth } from "@/common/hooks/useAuth";
import { ApiError } from "@/common/lib/api";
import {
  useAcceptInvitation,
  useInvitationPreview,
  useRejectInvitation,
} from "@/features/accounts/api/invitationsApi";
import { RoleBadge } from "@/features/accounts/components/RoleBadge";
import { writeCurrentAccountId } from "@/features/accounts/hooks/useCurrentAccount";
import {
  clearPendingInvitationToken,
  writePendingInvitationToken,
} from "@/features/auth/lib/storage";
import { useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

export function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t");
  const navigate = useNavigate();
  const auth = useAuth();

  // Step 1: immediately persist the token in localStorage
  useEffect(() => {
    if (token) {
      writePendingInvitationToken(token);
    }
  }, [token]);

  const previewQuery = useInvitationPreview(auth.status === "authenticated" ? token : null);

  const acceptMutation = useAcceptInvitation();
  const rejectMutation = useRejectInvitation();

  // No token in URL
  if (!token) {
    return (
      <ErrorScreen message="Esta URL de invitación no es válida. Pedile al admin un nuevo link." />
    );
  }

  // Auth loading
  if (auth.status === "loading") {
    return <LoadingScreen />;
  }

  // Not authenticated
  if (auth.status === "unauthenticated") {
    const loginUrl = `/login?redirect=${encodeURIComponent(`/invitations/accept?t=${token}`)}`;
    return (
      <Screen>
        <h1 className="font-medium text-2xl text-white tracking-tight">Aceptar invitación</h1>
        <p className="text-neutral-300 text-sm leading-relaxed">
          Te invitaron a unirte. Confirmá tu email para entrar.
        </p>
        <Link
          to={loginUrl}
          className="mt-2 flex h-12 items-center justify-center rounded-full bg-primary-500 px-6 font-medium text-brand-dark text-sm transition-colors hover:bg-primary-400"
        >
          Continuar
        </Link>
      </Screen>
    );
  }

  // Authenticated — preview loading
  if (previewQuery.isLoading) {
    return <LoadingScreen />;
  }

  // Preview error
  if (previewQuery.isError) {
    const err = previewQuery.error;
    const msg =
      err instanceof ApiError && err.status === 404
        ? "Esta invitación no es válida o expiró. Pedí una nueva al admin."
        : "No pudimos cargar la invitación. Intentá de nuevo.";
    return <ErrorScreen message={msg} showDashboardLink />;
  }

  const preview = previewQuery.data;
  if (!preview) return <LoadingScreen />;

  async function handleAccept() {
    if (!token) return;
    try {
      const result = await acceptMutation.mutateAsync(token);

      if (result.status === "already_member") {
        toast.info(`Ya eras miembro de ${preview?.account.name}.`);
      }

      writeCurrentAccountId(result.accountId);
      clearPendingInvitationToken();
      navigate("/app", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 410) {
        clearPendingInvitationToken();
        return;
      }
      if (err instanceof ApiError && err.status === 403 && err.code === "EMAIL_MISMATCH") {
        toast.error("Esta invitación es para otro email. Entrá con ese email.");
        return;
      }
      toast.error("No se pudo aceptar la invitación. Intentá de nuevo.");
    }
  }

  async function handleReject() {
    if (!token) return;
    try {
      await rejectMutation.mutateAsync(token);
    } catch {
      // Best-effort
    }
    clearPendingInvitationToken();
    navigate("/app", { replace: true });
  }

  const isBusy = acceptMutation.isPending || rejectMutation.isPending;

  // Show 410 errors inline after accept attempt
  if (acceptMutation.isError) {
    const err = acceptMutation.error;
    if (err instanceof ApiError && err.status === 410) {
      clearPendingInvitationToken();
      const msg =
        err.code === "INVITATION_AUTO_CANCELLED"
          ? "Esta invitación fue cancelada porque ya sos miembro de esa cuenta."
          : "Esta invitación ya no es válida.";
      return <ErrorScreen message={msg} showDashboardLink />;
    }
  }

  return (
    <>
      <title>Aceptar invitación · HATO</title>
      <meta name="robots" content="noindex, nofollow" />

      <div
        className="landing-texture pointer-events-none fixed inset-0 opacity-50"
        aria-hidden="true"
      />

      <main className="relative min-h-screen bg-brand-dark text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[90vh]"
          style={{
            background:
              "radial-gradient(circle at 50% 10%, rgba(0, 229, 153, 0.08) 0%, transparent 60%)",
          }}
        />

        <div className="relative mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-6 py-12">
          <Link to="/" className="mb-10 flex items-center gap-3 self-start">
            <MihatoMark className="h-7 w-7 text-white" />
            <span className="font-medium text-lg tracking-tight">HATO</span>
          </Link>

          <div className="flex flex-col gap-6 rounded-2xl border border-white/[0.06] bg-brand-panel p-6">
            <div className="flex flex-col gap-1">
              <h1 className="font-medium text-2xl text-white tracking-tight">
                Invitación a {preview.account.name}
              </h1>
              <p className="text-neutral-400 text-sm">
                {preview.inviter.email} te invitó a unirte como:
              </p>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-neutral-300 text-sm">Tu rol:</span>
              <RoleBadge role={preview.role} />
            </div>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={handleAccept}
                disabled={isBusy}
                className="flex h-12 items-center justify-center rounded-full bg-primary-500 px-6 font-medium text-brand-dark text-sm transition-colors hover:bg-primary-400 disabled:opacity-60"
              >
                {acceptMutation.isPending ? "Aceptando…" : "Aceptar"}
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={isBusy}
                className="flex h-12 items-center justify-center rounded-full border border-white/[0.1] px-6 font-medium text-sm text-white transition-colors hover:bg-white/[0.05] disabled:opacity-60"
              >
                {rejectMutation.isPending ? "Rechazando…" : "Rechazar"}
              </button>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

function Screen({ children }: { children: React.ReactNode }) {
  return (
    <>
      <title>Aceptar invitación · HATO</title>
      <meta name="robots" content="noindex, nofollow" />
      <main className="relative flex min-h-screen flex-col items-center justify-center bg-brand-dark px-6 py-12 text-center text-white">
        <Link to="/" className="mb-10 flex items-center gap-3">
          <MihatoMark className="h-7 w-7 text-white" />
          <span className="font-medium text-lg tracking-tight">HATO</span>
        </Link>
        <div className="flex w-full max-w-md flex-col gap-4 rounded-2xl border border-white/[0.06] bg-brand-panel p-6 text-left">
          {children}
        </div>
      </main>
    </>
  );
}

function LoadingScreen() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-brand-dark">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/[0.1] border-t-primary-500" />
    </main>
  );
}

function ErrorScreen({
  message,
  showDashboardLink,
}: {
  message: string;
  showDashboardLink?: boolean;
}) {
  return (
    <>
      <title>Invitación inválida · HATO</title>
      <main className="relative flex min-h-screen flex-col items-center justify-center bg-brand-dark px-6 py-12 text-center text-white">
        <Link to="/" className="mb-10 flex items-center gap-3">
          <MihatoMark className="h-7 w-7 text-white" />
          <span className="font-medium text-lg tracking-tight">HATO</span>
        </Link>
        <div className="flex w-full max-w-md flex-col gap-4 rounded-2xl border border-rose-500/20 bg-rose-500/[0.04] p-6">
          <p className="text-neutral-300 text-sm leading-relaxed">{message}</p>
          {showDashboardLink && (
            <Link
              to="/app"
              className="flex h-12 items-center justify-center rounded-full bg-primary-500 px-6 font-medium text-brand-dark text-sm transition-colors hover:bg-primary-400"
            >
              Ir al dashboard
            </Link>
          )}
        </div>
      </main>
    </>
  );
}
